import { dollarsToCents } from '../../../domain/Promotions/money';

// Dollars, decimal, up to 2 decimal places. A leading "$" is tolerated so a
// pasted "$25.00" is still accepted; anything else is flagged as bad_format.
export const AMOUNT_PATTERN = /^\$?\s*\d+(\.\d{1,2})?$/;

// Mirrors the BE upload skipped[].reason enum. The last two are surfaced by the
// BE at submit time — they are not produced client-side.
export const PAYOUT_REASONS = {
  MISSING_VALUE: 'missing_value',
  BAD_FORMAT: 'bad_format',
  AMOUNT_TOO_HIGH: 'amount_too_high',
  UNRECOGNIZED_PROFESSIONAL: 'unrecognized_professional',
  TECHNICAL_ERROR: 'technical_error',
};

// Human-readable copy for the review screen / failed report.
export const PAYOUT_REASON_LABELS = {
  [PAYOUT_REASONS.MISSING_VALUE]: 'Missing value',
  [PAYOUT_REASONS.BAD_FORMAT]: 'Wrong price format (expected $XX.XX)',
  [PAYOUT_REASONS.AMOUNT_TOO_HIGH]: 'Amount exceeds the promotion max',
  [PAYOUT_REASONS.UNRECOGNIZED_PROFESSIONAL]: 'Unrecognized professional ID',
  [PAYOUT_REASONS.TECHNICAL_ERROR]: 'Technical error',
};

const HEADER_KEY = 'professional_id';

// Parses raw CSV text into { professionalId, amount, line } rows. Two columns:
// professional_id, amount. A header row is optional and auto-detected when the
// first cell equals "professional_id" (case-insensitive) — matching the BE
// upload parser so the FE and BE agree on what counts as a data row.
export const parsePayoutCsv = (text) => {
  if (typeof text !== 'string' || text.trim() === '') return [];

  const rows = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => line.split(','));

  if (rows.length === 0) return [];

  const hasHeader = (rows[0][0] || '').trim().toLowerCase() === HEADER_KEY;
  const dataRows = hasHeader ? rows.slice(1) : rows;
  // 1-based line number in the original file (offset by 1 when a header exists).
  const offset = hasHeader ? 2 : 1;

  return dataRows.map((cells, index) => ({
    professionalId: (cells[0] || '').trim(),
    amount: (cells[1] || '').trim(),
    line: index + offset,
  }));
};

// Validates parsed rows against the promotion cap. Client-side checks only:
// missing_value, bad_format, amount_too_high. Professional-ID existence is
// validated by the BE at submit time and surfaces in the submission summary,
// so unrecognized IDs are NOT flagged here.
export const validatePayoutRows = (rows, maxValueCents) => {
  const evaluated = (rows || []).map((row) => {
    if (!row.professionalId || !row.amount) {
      return { ...row, valid: false, reason: PAYOUT_REASONS.MISSING_VALUE };
    }

    if (!AMOUNT_PATTERN.test(row.amount)) {
      return { ...row, valid: false, reason: PAYOUT_REASONS.BAD_FORMAT };
    }

    const amountCents = dollarsToCents(row.amount);
    if (Number.isNaN(amountCents)) {
      return { ...row, valid: false, reason: PAYOUT_REASONS.BAD_FORMAT };
    }

    if (
      typeof maxValueCents === 'number' &&
      maxValueCents > 0 &&
      amountCents > maxValueCents
    ) {
      return {
        ...row,
        amountCents,
        valid: false,
        reason: PAYOUT_REASONS.AMOUNT_TOO_HIGH,
      };
    }

    return { ...row, amountCents, valid: true };
  });

  return {
    rows: evaluated,
    valid: evaluated.filter((row) => row.valid),
    invalid: evaluated.filter((row) => !row.valid),
  };
};
